import React, {ReactElement} from "react";
import styles from "./TypeSelect.module.css";

interface SearchDropDownProps {
    emptyState?: React.ReactElement;
    children?: React.ReactNode;
    placeholder?: string;
    isLoading?: boolean | undefined;
    onChange?: (text: string) => void;
}


const TypeSelect = ({isLoading, placeholder, onChange, emptyState, children}: SearchDropDownProps) => {

    const [searchText, setSearchText] = React.useState("");
    const [isOpen, setIsOpen] = React.useState(false);
    const [selectedItemIndex, setSelectedItemIndex] = React.useState(-1);

    const containerDivRef = React.useRef<HTMLDivElement>()
    const searchBoxRef = React.useRef<HTMLInputElement>();

    /* List of valid children */
    let validChildren: ReactElement[] = [];
    if (Array.isArray(children))
        for (let child of children)
            if (child.type && child.type == 'li' && child.key && child.props?.children)
                validChildren.push(child);
    const haveValidChildren = validChildren.length > 0;

    React.useEffect(() => {
        setIsOpen(haveValidChildren || searchText.length > 0 && !isLoading);
    }, [haveValidChildren, searchText, isLoading])

    const incSelectedItemIndex = () => {
        if (selectedItemIndex === validChildren.length - 1)
            setSelectedItemIndex(0);
        else
            setSelectedItemIndex((selectedItemIndex + 1) % validChildren.length)
    }

    const decSelectedItemIndex = () => {
        if (selectedItemIndex === 0)
            setSelectedItemIndex(validChildren.length - 1)
        else
            setSelectedItemIndex((selectedItemIndex - 1) % validChildren.length);
    }

    const changeText = (text: string) => {
        if (onChange)
            onChange(text);
        setSearchText(text)
        setSelectedItemIndex(-1);
    }

    const selectItem = (_: number) => {
        // if (onSelect)
        //     onSelect(i);
        changeText("");
        setIsOpen(false);
    }

    /* Drop down click handling */
    const inputOnClick = (_: React.MouseEvent<HTMLInputElement>) => {
        if (!isOpen)
            if (haveValidChildren)
                setIsOpen(true);
        else
            setIsOpen(false);
    };

    /* Specific keypress handling */
    const inputOnKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        const key = e.key.toLocaleLowerCase();
        if (key === 'escape')
            setIsOpen(false)
        else if (key === 'arrowup'){
            decSelectedItemIndex();
            e.preventDefault();
        } else if (key === 'arrowdown') {
            incSelectedItemIndex();
            e.preventDefault();
        } else if (key === 'enter'){
            if (selectedItemIndex > -1){
                setIsOpen(false);
                selectItem(selectedItemIndex);
                e.preventDefault();
            }
        } else if (key == "pageup") {
            setSelectedItemIndex(0);
        } else if (key == "pagedown") {
            setSelectedItemIndex(validChildren.length - 1);
        }
    }

    /* Click outside handling */
    React.useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            const ref = containerDivRef;
            if (ref.current && !ref.current.contains(e.target as any)){
                setIsOpen(false);
                setSelectedItemIndex(-1);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside)
    });

    /* Input box */
    const inputBox = <input
        ref={searchBoxRef}
        type="text" placeholder={placeholder}
        className={`${styles.searchBox} ${isOpen && styles.open}`}
        value={searchText}
        onChange={(e) => changeText(e.target.value)}
        onClick={inputOnClick}
        onKeyDown={inputOnKeyDown}
    />

    /* Drop down item to show when loading */
    const loadingItem = <li className={styles.dropDownItem}>
        <label className={styles.dropDownItemLabel}>
            <span>Loading ...</span>
        </label>
    </li>

    /* Drop down items */
    const dropDownName = React.useId();
    let dropDownItems: ReactElement[] = [];

    const itemOnChange = (e: any) => {
        const index = Number(e.target.value);
        setSelectedItemIndex(index);
    };

    const itemOnClick = (e: any) => {
        const index = Number(e.target.value);
        selectItem(index);
        setIsOpen(false);
    }

    const selectedItemRef = React.useRef<HTMLLIElement | undefined>();
    for (let i = 0; i < validChildren.length; i++) {
        const checked = selectedItemIndex === i;
        const child = validChildren[i];
        const refProp = checked ? {ref: selectedItemRef} : {};

        dropDownItems.push(
            <li key={child.key} className={styles.dropDownItem} {...refProp}>
                <input className={styles.dropDownItemInput} type="radio" name={dropDownName} id={child.key}
                       value={i} checked={checked} onChange={itemOnChange} onClick={itemOnClick}/>
                <label className={styles.dropDownItemLabel} htmlFor={child.key}>
                    {child.props.children}
                </label>
            </li>);
    }

    // Scroll to checked element after render
    React.useEffect(() => selectedItemRef.current?.scrollIntoView());

    /* Drop down menu */
    const dropDown = (
        <ul className={`${styles.dropDown} ${isOpen && styles.open}`}>
            { searchText.length > 0 &&
                isLoading
                    ? <></>
                    : dropDownItems.length > 0
                        ? dropDownItems
                        : emptyState
            }
        </ul>
    );

    return <>
        <div className={styles.container} ref={containerDivRef}>
            {inputBox}
            {dropDown}
        </div>
    </>
}

export default TypeSelect;